// 📦 src/components/ProductDetail.jsx

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AddToCartButton from "./AddToCartButton";
import { shopifyHelpers } from "../utils/shopify";
import bgtrace from "../assets/bgtrace1.jpeg";

const getVariantIdForCart = (variantId) => {
  if (!variantId) return null;
  const str = String(variantId);
  if (str.includes("/")) return str.split("/").pop();
  return str;
};

const ProductDetail = () => {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [mainImage, setMainImage] = useState("/placeholder.png");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const current = await shopifyHelpers.getProductByHandle(handle);
        setProduct(current);
        setMainImage(current.selectedImage);

        if (current.variants?.length) {
          const firstAvailable =
            current.variants.find((v) => v.availableForSale) ||
            current.variants[0];
          setSelectedVariant(firstAvailable);
        } else if (current.variantId) {
          setSelectedVariant({
            id: current.variantId,
            availableForSale: true,
            title: current.title,
          });
        }

        const allProducts = await shopifyHelpers.getAllProducts(50);
        const filtered = allProducts.filter((p) => p.handle !== handle);
        setRelated(filtered.slice(0, 3));
      } catch (err) {
        console.error("❌ Error loading product detail:", err);
      } finally {
        setLoading(false);
      }
    }

    if (handle) fetchData();
  }, [handle]);

  const handleVariantChange = (variantId) => {
    const variant = product.variants.find((v) => v.id === variantId);
    setSelectedVariant(variant);

    if (variant?.image) {
      setMainImage(variant.image);
    } else if (product.images?.length > 0) {
      setMainImage(product.images[0]?.url || product.selectedImage);
    }
  };

  if (loading)
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${bgtrace})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="bg-neutralgray backdrop-blur-sm rounded-lg px-8 py-4 shadow-md shadow-black/40 border border-neutral-700/50">
          <p className="text-gray-100">Loading...</p>
        </div>
      </div>
    );

  if (!product)
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${bgtrace})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="bg-neutralgray backdrop-blur-sm rounded-lg px-8 py-4 shadow-md shadow-black/40 border border-neutral-700/50">
          <p className="text-red-400">Product not found.</p>
        </div>
      </div>
    );

  return (
    <main 
      className="min-h-screen"
      style={{
        backgroundImage: `url(${bgtrace})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Product Hero Section */}
      <section 
        id="product-hero"
        className="relative scroll-pt-24 container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 md:pt-16 lg:pt-20 xl:pt-24 pb-8 md:pb-12"
      >
        {/* Product Showcase Card */}
        <div className="bg-gradient-to-b from-neutralgray to-neutraldark backdrop-blur-sm rounded-2xl shadow-md shadow-black/40 p-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-start border border-neutral-700/50 mt-0 max-w-6xl mx-auto">
        {/* Product Image */}
        <div className="flex flex-col gap-3">
          <img
            src={mainImage}
            alt={product.title}
            className="w-full h-96 object-cover rounded-xl shadow-md max-w-md transition-opacity duration-200"
          />
          {product.images?.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={img.url}
                  alt={img.altText || product.title}
                  onClick={() => setMainImage(img.url)}
                  className={`w-20 h-20 object-cover rounded-lg cursor-pointer border-2 transition ${
                    mainImage === img.url
                      ? "border-brandred ring-2 ring-brandred/50"
                      : "border-neutral-600 hover:border-neutral-500"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-4xl font-bold mb-4 text-gray-100">{product.title}</h1>

          <div className="text-2xl font-semibold text-brandred mb-6">
            ${Number(product.price).toFixed(2)}
          </div>

          <p className="text-gray-300 leading-relaxed mb-6">
            {product.description}
          </p>

          {/* ===== Variant Selector ===== */}
          {product.variants?.length > 0 && (
            <div className="mb-6">
              <div className="text-sm font-semibold text-gray-200 mb-2">
                {product.options?.[0]?.name || "Options"}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => {
                  const isActive = selectedVariant?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      disabled={!v.availableForSale}
                      onClick={() => handleVariantChange(v.id)}
                      className={[
                        "px-3 py-2 rounded-md border text-sm transition",
                        isActive
                          ? "border-brandred bg-brandred text-white"
                          : "border-neutral-600 bg-neutral-700 text-gray-100 hover:bg-neutral-600",
                        !v.availableForSale
                          ? "opacity-50 cursor-not-allowed"
                          : "",
                      ].join(" ")}
                    >
                      {v.title} {!v.availableForSale && "(Sold out)"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add to Cart Button */}
          {selectedVariant && (
            <div className="mb-6">
              <AddToCartButton
                variantId={getVariantIdForCart(selectedVariant.id)}
                available={selectedVariant.availableForSale}
              />
            </div>
          )}

          {/* Trust Badges */}
          <div className="flex flex-col sm:flex-row gap-4 text-gray-300 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-green-400">✅</span> Secure Checkout
            </div>
            <div className="flex items-center gap-2">
              <span className="text-yellow-400">🚚</span> Fast Shipping
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-400">📞</span> Expert Support
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Related Products Section */}
      {related.length > 0 && (
        <section 
          id="related-products"
          className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20"
        >
          <div className="max-w-6xl mx-auto border-t border-neutral-600 pt-10">
            <h2 className="text-2xl font-bold mb-6 text-black">
              You may also like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/product/${item.handle}`}
                  className="bg-gradient-to-b from-neutralgray to-neutraldark backdrop-blur-sm rounded-xl p-6 shadow-md shadow-black/40 text-center transition-all duration-300 border border-neutral-700/50 hover:shadow-xl hover:border-neutral-600"
                >
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="w-full h-40 object-contain mb-4 rounded-md"
                  />
                  <h3 className="text-gray-100 font-semibold">{item.title}</h3>
                  <p className="text-brandred font-bold mt-2">
                    {item.price
                      ? `$${Number(item.price).toFixed(2)}`
                      : "Price unavailable"}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
